import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue as mode,
} from '@chakra-ui/react';
import AmatriumSkeleton from 'components/AmatriumSkeleton';
import Empty from 'components/Empty';
import { dateFormat } from 'helpers/date';
import React, { useMemo } from 'react';

interface AmatriumTableV2Props {
  columns: any[];
  data: any[];
  isLoading: boolean;
  rowKey?: string;
  onRowSelect?: (_row: any, _idRow: number) => void;
  borderWidth?: string;
}

const AmatriumTableV2 = (props: AmatriumTableV2Props) => {
  const { columns, data, isLoading, rowKey, onRowSelect } = props;
  const theadBackgroundColor = mode('rgb(250, 249, 247)', '');
  const trBackgroundColor = mode('grey.200', 'grey.500');
  const scrollbarThumbBg = mode('gray.300', 'gray.600');
  const scrollbarThumbHoverBg = mode('gray.400', 'gray.500');
  const borderColor = mode('gray.200', 'gray.600');

  // Add blank rows if data length is less than 10
  const processedData = useMemo(() => {
    if (data?.length && data.length > 0 && data.length < 10) {
      const arrNotHaveData = [];
      for (let i = 0; i < 10 - data.length; i++) {
        // Create a blank row object with proper structure to avoid "UNLIMITED" display
        const objNotHaveData = {
          id: `blank_${i}`,
          name: '',
          user_count: '',
          limitation: {
            storage_gb: 0, // Use 0 instead of null to avoid "UNLIMITED"
            question: 0,
            translation: 0,
            expiration_date: '',
          },
          current_usage: {
            total_storage_gb: 0,
            total_questions: 0,
            total_translations: 0,
          },
          expiration_date: '',
          isBlank: true, // Flag to identify blank rows
        };
        arrNotHaveData.push(objNotHaveData);
      }
      return [...data, ...arrNotHaveData];
    }
    return data || [];
  }, [data]);

  const showText = (col: any, _row: any) => {
    return col.render ? (
      col.render(_row)
    ) : _row[col.key] && col.key === 'created_date' ? (
      dateFormat(_row[col.key])
    ) : _row[col.key] !== '' ? (
      _row[col.key]
    ) : (
      <Box py={{ base: '0.55rem' }} color='#111827' fontSize={14} />
    );
  };

  return (
    <Box>
      {isLoading ? (
        <AmatriumSkeleton options={[]} />
      ) : processedData?.length ? (
        <Box overflowX='auto' w='100%'>
          <Box overflowY='auto' maxH='60vh'>
            <Table
              sx={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: scrollbarThumbBg,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: scrollbarThumbHoverBg,
                },
                '& thead': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                },
                '& th': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  borderBottom: '1px solid',
                  borderColor: borderColor,
                },
              }}
              display={{
                base: 'block',
                md: 'table',
              }}
              bg='bg_box'
              borderWidth={props.borderWidth ?? '0'}
              style={{ width: '100%', minWidth: 'max-content' }}
            >
              <Thead>
                <Tr>
                  {columns?.map(
                    (col) =>
                      col?.key && (
                        <Th
                          key={col.key}
                          px={3}
                          py={{
                            base: '0.5rem',
                            lg: '0.75rem',
                          }}
                          color='color_main_text'
                          fontSize={'0.8rem'}
                          fontWeight='500'
                          textTransform={'none'}
                          bg={theadBackgroundColor}
                          whiteSpace='nowrap'
                        >
                          {col.title}
                        </Th>
                      )
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {processedData?.map((row: any, idRow: number) => {
                  return (
                    <Tr
                      key={rowKey ? row[rowKey] : row?.key ? row.key : idRow}
                      bg={row?.active ? 'grey.500' : ''}
                      _hover={
                        onRowSelect
                          ? {
                              bg: trBackgroundColor,
                              cursor: 'pointer',
                            }
                          : {}
                      }
                      onClick={() => {
                        if (onRowSelect) {
                          onRowSelect(row, idRow);
                        }
                      }}
                    >
                      {columns?.map((col) => {
                        return col?.key ? (
                          <Td key={col?.key} px={3} py={2} fontSize={13} whiteSpace='nowrap'>
                            {col?.width && !col?.notShowTooltip ? (
                              <Tooltip label={showText(col, row)} placement='bottom-start'>
                                {col.render ? (
                                  col.render(row)
                                ) : (
                                  <Text isTruncated>
                                    {row[col.key] && col.key === 'created_date' ? (
                                      dateFormat(row[col.key])
                                    ) : row[col.key] !== '' ? (
                                      row[col.key]
                                    ) : (
                                      <Box py={{ base: '0.55rem' }} color='#111827' fontSize={13} />
                                    )}
                                  </Text>
                                )}
                              </Tooltip>
                            ) : col.render ? (
                              col.render(row)
                            ) : (
                              <Text>
                                {row[col.key] && col.key === 'created_date' ? (
                                  dateFormat(row[col.key])
                                ) : row[col.key] !== '' ? (
                                  row[col.key]
                                ) : (
                                  <Box py={{ base: '0.55rem' }} fontSize={13} />
                                )}
                              </Text>
                            )}
                          </Td>
                        ) : null;
                      })}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </Box>
      ) : (
        <Empty content={null} />
      )}
    </Box>
  );
};

export default AmatriumTableV2;
